import { useState, useEffect } from "react";
import { X, MessageSquare, Loader } from "lucide-react";
import { toast } from "react-toastify";
import { sendToDiscord } from "../services/DiscordService";
import type { StoreProductResult } from "../services/ProductSearchService";
import {
  computeSavings,
  getStoreConfig,
  validateDiscordSubmission,
} from "./DiscordPriceModal.utils";
import { getProductImageUrl } from "../utils/imageUtils";

interface DiscordPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: StoreProductResult | null;
  retailer?: string; // "walmart" or "home_depot"
  zipCode?: string;
}

export default function DiscordPriceModal({
  isOpen,
  onClose,
  product,
  retailer = "walmart",
  zipCode,
}: DiscordPriceModalProps) {
  const [msrp, setMsrp] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [isSending, setIsSending] = useState(false);
  const savings = computeSavings(msrp, salePrice);

  // Initialize values when modal opens with a product
  useEffect(() => {
    if (isOpen && product) {
      const msrpValue = retailer === "walmart" ? (product.onlinePrice || 0) : (product.msrp || 0);
      setMsrp(msrpValue.toFixed(2));
      setSalePrice((product.storePrice || product.onlinePrice || 0).toFixed(2));
    }
  }, [isOpen, product, retailer]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleSendToDiscord = async () => {
    if (!product) return;

    const validationError = validateDiscordSubmission({
      name: product.name,
      sku: product.sku,
      msrp,
      salePrice,
    });
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const msrpValue = parseFloat(msrp);
    const salePriceValue = parseFloat(salePrice);
    const { storeName, textChannel } = getStoreConfig(retailer);

    setIsSending(true);

    try {
      await sendToDiscord({
        itemName: product.name.trim(),
        oldPrice: msrpValue,
        newPrice: salePriceValue,
        upc: product.sku.trim(),
        imageUrl: product.image || "",
        storeName: storeName,
        description: product.brand || "",
        textChannel: textChannel,
        includeBarcode: "Y",
        retailer: retailer,
        zipCode: zipCode?.trim() || undefined,
      });

      toast.success("Successfully sent to Discord!");
      onClose();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to send to Discord";
      toast.error(message);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    if (!isSending) {
      onClose();
    }
  };

  if (!isOpen || !product) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onPointerDown={handleClose}
    >
      <div
        className="w-full max-w-md mx-auto bg-white dark:bg-dark-800 rounded-2xl shadow-2xl overflow-hidden"
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Send to Discord
            </h3>
          </div>
          <button
            onPointerDown={handleClose}
            disabled={isSending}
            className="inline-flex items-center justify-center w-8 h-8 text-gray-500 dark:text-dark-400 hover:text-gray-700 dark:hover:text-dark-200 hover:bg-gray-200 dark:hover:bg-dark-700 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4">
          {/* Product Info */}
          <div className="flex items-start gap-3 mb-4 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
            {product.image ? (
              <img
                src={getProductImageUrl(product.image, "400")}
                alt={product.name}
                className="w-16 h-16 object-cover rounded-lg border border-gray-200 dark:border-dark-600 flex-shrink-0"
              />
            ) : (
              <div className="w-16 h-16 bg-gray-200 dark:bg-dark-600 rounded-lg border border-gray-200 dark:border-dark-600 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-6 h-6 text-gray-400 dark:text-dark-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2 mb-1">
                {product.name}
              </h4>
              <p className="text-xs text-gray-600 dark:text-dark-300">UPC: {product.sku}</p>
              <p className="text-xs text-gray-600 dark:text-dark-300">Brand: {product.brand}</p>
            </div>
          </div>

          {/* Price Editing Form */}
          <div className="space-y-4">
            <div>
              <label
                htmlFor="msrp"
                className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1"
              >
                MSRP (Original Price)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-dark-400 text-sm">
                  $
                </span>
                <input
                  id="msrp"
                  type="number"
                  step="0.01"
                  min="0"
                  value={msrp}
                  onChange={(e) => setMsrp(e.target.value)}
                  disabled={isSending}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="salePrice"
                className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1"
              >
                Sale Price (Current Price)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-dark-400 text-sm">
                  $
                </span>
                <input
                  id="salePrice"
                  type="number"
                  step="0.01"
                  min="0"
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  disabled={isSending}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Savings Display */}
            {msrp && salePrice && savings && (
                <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-700 dark:text-green-300 font-medium">Savings:</span>
                    <span className="text-green-700 dark:text-green-300 font-bold">
                      ${savings.amount.toFixed(2)}
                      {(savings.percent || 0) > 0 && (
                        <span className="text-xs ml-1">
                          (
                          {savings.percent}
                          % off)
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900">
          <button
            onPointerDown={handleClose}
            disabled={isSending}
            className="px-4 py-2 text-gray-700 dark:text-dark-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-dark-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onPointerDown={handleSendToDiscord}
            disabled={isSending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSending ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <MessageSquare className="w-4 h-4" />
                Send to Discord
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
